import { useEffect, useMemo, useState } from "react";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";

import { getTouchpoint } from "generated/api/touchpoint";
import { normalizeTouchpointListResponse } from "utilities/api-mappers";

import { CustomFormField } from "utilities/forms";
import { getCampaignPaths } from "utilities/app-routes";

const touchpointApi = getTouchpoint();

import { TouchpointType } from "types/touchpoint";

interface RedirectionOption {
  id: string;
  name: string;
}

const templateFields: CustomFormField[] = [
  {
    name: "advancedHeadSnippet",
    component: "textarea",
    label: "Head Snippet",
    helperText: "Scripts are applied directly before the closing </head> tag",
  },
  {
    name: "advancedBodySnippet",
    component: "textarea",
    label: "Body Snippet",
    helperText: "Scripts are applied directly before the closing </body> tag",
  },
];

interface UseLPSettingsFieldsParams {
  isTemplate: boolean;
}

const useLPSettingsFields = ({ isTemplate }: UseLPSettingsFieldsParams) => {
  const currentLp = useCurrentTouchpoint();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();

  const urlAddonStart = useMemo(() => {
    const domain = currentClient.apolloDomainUrl?.replace(/\/+$/, "");
    const cdn = currentCampaign.cdnSubdirectoryName?.trim();

    if (cdn) {
      return `${[domain, cdn].filter(Boolean).join("/")}/`;
    }

    return (
      <>
        {domain}/<span className="text-critical-700 font-medium">Not set</span>/
      </>
    );
  }, [currentClient.apolloDomainUrl, currentCampaign.cdnSubdirectoryName]);

  const { campaignSettingsEditPath } = useMemo(
    () => getCampaignPaths({ clientId: currentClient.id, campaignId: currentCampaign.id }),
    [currentClient.id, currentCampaign.id],
  );

  // CDN Profile validation — shown on the URL field when the campaign has no CDN subdirectory.
  // The renderError adds a link to campaign settings so the user can resolve it directly.
  const cdnProfileValidation = useMemo(() => {
    const missingCdnProfileMessage =
      "Please set the Campaign CDN Profile before entering a Page URL.";
    const isCdnProfileSet = !!currentCampaign?.cdnSubdirectoryName?.trim();

    return {
      validate: () => (isCdnProfileSet ? true : missingCdnProfileMessage),
      renderError: (message: string) => {
        if (message !== missingCdnProfileMessage) return message;
        return (
          <>
            <a
              href={campaignSettingsEditPath}
              className="underline font-medium hover:no-underline focus:outline-none focus:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Please set the Campaign CDN Profile
            </a>{" "}
            <span>before entering a Page URL.</span>
          </>
        );
      },
    };
  }, [currentCampaign?.cdnSubdirectoryName, campaignSettingsEditPath]);

  const [redirectOptions, setRedirectOptions] = useState<RedirectionOption[]>([]);

  useEffect(() => {
    const fetchRedirectOptions = async () => {
      try {
        const response = await touchpointApi.getAllTouchpointsForCampaign(
          { clientId: currentClient.id, campaignId: currentCampaign.id },
          { sort: ["name,asc"], unpaged: true },
        );
        const normalized = normalizeTouchpointListResponse(
          response,
          currentClient.id,
          currentCampaign.id,
        );

        setRedirectOptions(
          normalized.items
            .filter(({ type }) => type === TouchpointType.LANDING_PAGE)
            .filter(({ id }) => id !== currentLp.id),
        );
      } catch (e) {
        console.error("Error while fetching redirection options", e);
      }
    };
    fetchRedirectOptions();
  }, [currentCampaign.id, currentClient.id, currentLp.id]);

  const contentSettingsFields = useMemo<CustomFormField[]>(
    () => [
      {
        name: "pageTitle",
        component: "input",
        label: "Page Title",
        validations: { isRequired: true },
      },
      {
        name: "url",
        component: "input",
        label: "Page URL",
        helperText: "Enter the root folder of your landing page",
        placeholder: "folder-name",
        addonStart: urlAddonStart,
        validations: {
          isRequired: true,
          ...cdnProfileValidation,
        },
      },
      {
        name: "pageDescription",
        component: "textarea",
        label: "Page Description",
      },
      {
        name: "redirectDestinationSelect",
        component: "select",
        label: "Redirect destination",
        placeholder: "Select destination",
        options: [
          { name: "Custom URL", value: "custom-url" },
          { name: "No redirect", value: "no-redirect" },
          ...redirectOptions.map((option) => ({ name: option.name, value: option.id })),
        ],
      },
      {
        name: "redirectDestination",
        component: "input",
        label: "Custom redirect URL",
        placeholder: "https://www.example.edu/landing-page",
        validations: {
          isRequired: true,
          isValidUrl: true,
          isValidUrlValidationMessage: "Please enter a valid URL (e.g., https://www.example.com)",
        },
        // field will only be displayed if redirectDestinationSelect value is "custom-url"
        condition: (watch) => watch.redirectDestinationSelect === "custom-url",
      },
      {
        name: "redirectDestinationType",
        component: "hidden",
        defaultValue: "NONE",
      },
      {
        name: "advancedHeadSnippet",
        component: "textarea",
        label: "Head Snippet",
        helperText: "Scripts are applied directly before the closing </head> tag",
      },
      {
        name: "advancedBodySnippet",
        component: "textarea",
        label: "Body Snippet",
        helperText: "Scripts are applied directly before the closing </body> tag",
      },
    ],
    [redirectOptions, urlAddonStart, cdnProfileValidation],
  );

  return isTemplate ? templateFields : contentSettingsFields;
};

export default useLPSettingsFields;
